import { BaseApiResponse } from '@core/swagger-client';
import { FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { ErrorMessage } from '../ErrorMessage';
import { AltText } from '../Icons';
import { TextArea } from '../TextArea';

/** The backend's limit (ImageAsset.MaxAltTextLength) */
const maxLength = 250;

interface Props {
    /** The image's current alt text; none means decorative */
    altText?: string | null;
    /** Stores the text (empty makes the image decorative); a rejection keeps the dialog open with the error */
    onSave: (altText: string) => Promise<unknown>;
}

/**
 * Button + dialog to set an image's alt text (WCAG 1.1.1), next to the upload and delete buttons of a profile image.
 * A dialog rather than a field under the image: the banner sits there, and the dialog has room for an explanation.
 */
export const ImageAltText = ({ altText, onSave }: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<BaseApiResponse>();
    const explanationId = useId();

    const show = () => {
        setValue(altText ?? '');
        setError(undefined);
        setOpen(true);
    };

    const save = async (e: FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(undefined);
        try {
            await onSave(value);
            setOpen(false);
        } catch (err) {
            // The API client rejects with the response, which ErrorMessage turns into a translated message
            setError(err as BaseApiResponse);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button variant="primary-outline" onClick={show} aria-haspopup="dialog">
                <AltText classNames="size-6 flex-none stroke-current menu:me-2" />
                <span className="sr-only menu:not-sr-only">{t('Image.AltText')}</span>
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} title={t('Image.AltText')}>
                <form onSubmit={save}>
                    <p id={explanationId} className="mb-6">
                        {t('Image.AltText.Explanation')}
                    </p>
                    <TextArea
                        label={t('Image.AltText')}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        maxLength={maxLength}
                        aria-describedby={explanationId}
                        classNames="mb-6"
                    />
                    {error && <ErrorMessage apiError={error} classNames="mb-6" />}
                    <div className="flex flex-wrap justify-end gap-4">
                        <Button type="button" variant="primary-outline" onClick={() => setOpen(false)}>
                            {t('Generic.Button.Cancel')}
                        </Button>
                        <Button type="submit" variant="primary" isLoading={saving}>
                            {t('Generic.Button.Save')}
                        </Button>
                    </div>
                </form>
            </Dialog>
        </>
    );
};
